// @flow

import { graphql, useFragment } from 'react-relay';
import { type Descriptor, createDescriptor } from '../../controls/router';
import { localeToPlaceLang } from '../../utils/localeToLng';
import type { listingsQueryVariables } from '../../pages/__generated__/listingsQuery.graphql';
import type { listingsDescriptorFiltersToParams_filters$key } from './__generated__/listingsDescriptorFiltersToParams_filters.graphql';
import {
  type OfferTypeFilterParams,
  offerTypeFilterDescriptor,
  type RoomsFilterParams,
  roomsFilterDescriptor,
  type PortalFilterParams,
  portalFilterDescriptor,
  type AgencyFilterParams,
  agencyFilterDescriptor,
  type AgencyNameFilterParams,
  agencyNameFilterDescriptor,
  type OnlineSinceFilterParams,
  onlineSinceFilterDescriptor,
  type SurfaceFilterParams,
  surfaceFilterDescriptor,
  type TopFilterParams,
  topFilterDescriptor,
  type PropertyTypeFilterParams,
  type OtherFilterParams,
  otherFilterDescriptor,
  type PriceFilterParams,
  priceFilterDescriptor,
  type PageParams,
  pageDescriptor,
  type SortParams,
  sortDescriptor,
  type RentPricePerAdditionalParams,
  rentPricePerAdditionalDescriptor,
  type SalePricePerSurfaceParams,
  salePricePerSurfaceDescriptor,
  type MapBoundsParams,
  mapBoundsDescriptor,
  type ShowMapParams,
  showMapDescriptor,
  type ListingSlugParams,
  listingSlugDescriptor,
  propertyTypeFilterDescriptor,
  type LandSurfaceFilterParams,
  landSurfaceFilterDescriptor,
} from './Filters/sharedDescriptors';
import { APPRAISABLE_PROPERTY_TYPES } from './consts';

type ListingsFilterMobileParams = {|
  ...RoomsFilterParams,
  ...PortalFilterParams,
  ...AgencyFilterParams,
  ...AgencyNameFilterParams,
  ...OnlineSinceFilterParams,
  ...SurfaceFilterParams,
  ...PropertyTypeFilterParams,
  ...OtherFilterParams,
  ...LandSurfaceFilterParams,
  ...SalePricePerSurfaceParams,
  ...RentPricePerAdditionalParams,
  ...PriceFilterParams,
|};

export const listingsFilterMobileDescriptor: Descriptor<ListingsFilterMobileParams> =
  createDescriptor(
    roomsFilterDescriptor,
    portalFilterDescriptor,
    agencyFilterDescriptor,
    agencyNameFilterDescriptor,
    onlineSinceFilterDescriptor,
    surfaceFilterDescriptor,
    propertyTypeFilterDescriptor,
    otherFilterDescriptor,
    landSurfaceFilterDescriptor,
    salePricePerSurfaceDescriptor,
    rentPricePerAdditionalDescriptor,
    priceFilterDescriptor,
  );

type ListingsDataParams = {|
  ...TopFilterParams,
  ...ListingsFilterMobileParams,
  ...OfferTypeFilterParams,
  ...MapBoundsParams,
  ...SortParams,
  ...PageParams,
  ...ListingSlugParams,
|};

export type ListingsParams = {|
  ...ListingsDataParams,
  ...ShowMapParams,
|};

export const listingsDescriptor: Descriptor<ListingsParams> = createDescriptor(
  topFilterDescriptor,
  listingsFilterMobileDescriptor,
  offerTypeFilterDescriptor,
  mapBoundsDescriptor,
  sortDescriptor,
  pageDescriptor,
  showMapDescriptor,
  listingSlugDescriptor,
);

// To intecept only changes which affected on query
export const listingsDataDescriptor: Descriptor<ListingsDataParams> =
  createDescriptor(
    topFilterDescriptor,
    listingsFilterMobileDescriptor,
    offerTypeFilterDescriptor,
    mapBoundsDescriptor,
    sortDescriptor,
    pageDescriptor,
    listingSlugDescriptor,
  );

// used to calculate hash on mobile to restore position on list
// the idea is to restore mobile list position when we come from map
// only if filters affecting list results didn't changed
export const listingsMobileListDescriptor: Descriptor<{|
  ...TopFilterParams,
  ...ListingsFilterMobileParams,
  ...OfferTypeFilterParams,
  ...MapBoundsParams,
  ...SortParams,
  ...PageParams,
|}> = createDescriptor(
  topFilterDescriptor,
  listingsFilterMobileDescriptor,
  offerTypeFilterDescriptor,
  mapBoundsDescriptor,
  sortDescriptor,
  pageDescriptor,
);

export const getItemsPerPage = (
  offerType: string,
  propertyType: ?string,
): number => {
  const ITEMS_PER_PAGE: number = 24;
  if (
    offerType === 'buy' &&
    APPRAISABLE_PROPERTY_TYPES.includes(propertyType)
  ) {
    // -1 since we will insert a valuation card in the results.
    return ITEMS_PER_PAGE - 1;
  }
  return ITEMS_PER_PAGE;
};

const pickBy = <T: Object>(obj: T, pick): T =>
  (Object.entries(obj)
    .filter(([, v]) => pick(v))
    .reduce(
      (r, [k, v]) => ({
        ...r,
        [k]: v,
      }),
      {},
    ): any);

const getSEODataGraphQLArgs = (
  params: ListingsParams,
): {|
  includePlace: boolean,
  slug: string,
  includeCountry: boolean,
|} => {
  const baseArgs = {
    includePlace: false,
    slug: '',
    includeCountry: false,
  };

  const placeSlugs = params.placeSlugs ?? [];

  if (placeSlugs.length === 0) {
    return { ...baseArgs, includeCountry: true };
  }

  if (placeSlugs.length === 1) {
    const place = placeSlugs[0];

    // improve this
    return {
      ...baseArgs,
      includePlace: true,
      slug: place.slug,
    };
  }

  return baseArgs;
};

export const useFiltersToParams = (
  filtersRef: listingsDescriptorFiltersToParams_filters$key | void,
): $Shape<ListingsParams> => {
  const filters = useFragment(
    graphql`
      fragment listingsDescriptorFiltersToParams_filters on AggregatesListingFilters {
        offerType_eq
        places {
          slug
          lang
          type
        }
        compositePropertyType_eq
        livingSurface_lte
        livingSurface_gte
        landSurface_lte
        landSurface_gte
        hasBalcony_eq
        hasGarden_eq
        hasParking_eq
        hasElevator_eq
        hasView_eq
        hasFireplace_eq
        hasGarage_eq
        isChildFriendly_eq
        isWheelchairAccessible_eq
        isMinergieCertified_eq
        isNewDevelopment_eq
        isFurnished_eq
        bookOnline_eq
        isFsbo_eq
        salePrice_lte
        salePrice_gte
        grossRentMonthly_lte
        grossRentMonthly_gte
        salePricePerLivingSurface_gte
        salePricePerLivingSurface_lte
        agencyReference_contains
        agencyNames
        salePricePerLandSurface_lte
        salePricePerLandSurface_gte
        grossRentRoomMonthly_lte
        grossRentRoomMonthly_gte
        grossRentM2yearly_lte
        grossRentM2yearly_gte
        numberOfRooms_gte
        numberOfRooms_lte
        portal_in
        createdAt_gte
      }
    `,
    filtersRef,
  );

  if (filters == null) {
    return {};
  }

  // We intentionally build fullParams first and only then paramsShape,
  // so that if any param is missing Flow will warn us
  const fullParams: ListingsParams = {
    east: null,
    west: null,
    north: null,
    south: null,
    page: 0,
    sortBy: null,
    sortDirection: null,
    showMap: true,
    listingSlug: '',
    offerType: filters.offerType_eq,
    // filters.placeSlugs is not always populated,
    // but we can rely on filters.places to be there
    placeSlugs: (filters.places ?? [])
      // exclude county level places because it's set by default
      .filter(place => place.type !== 'country')
      .map(({ slug, lang }) => ({ slug, lang })),
    portal_in: (filters.portal_in ?? []).filter(Boolean),
    compositePropertyType_eq: filters.compositePropertyType_eq ?? null,
    agencyReference_contains: filters.agencyReference_contains ?? null,
    agencyNames: filters.agencyNames ?? null,
    grossRentM2yearly_gte: filters.grossRentM2yearly_gte ?? null,
    grossRentM2yearly_lte: filters.grossRentM2yearly_lte ?? null,
    grossRentMonthly_gte: filters.grossRentMonthly_gte ?? null,
    grossRentMonthly_lte: filters.grossRentMonthly_lte ?? null,
    hasBalcony_eq: filters.hasBalcony_eq ?? null,
    hasElevator_eq: filters.hasElevator_eq ?? null,
    hasFireplace_eq: filters.hasFireplace_eq ?? null,
    hasGarage_eq: filters.hasGarage_eq ?? null,
    hasGarden_eq: filters.hasGarden_eq ?? null,
    hasParking_eq: filters.hasParking_eq ?? null,
    hasView_eq: filters.hasView_eq ?? null,
    isChildFriendly_eq: filters.isChildFriendly_eq ?? null,
    isMinergieCertified_eq: filters.isMinergieCertified_eq ?? null,
    isNewDevelopment_eq: filters.isNewDevelopment_eq ?? null,
    isFurnished_eq: filters.isFurnished_eq ?? null,
    bookOnline_eq: filters.bookOnline_eq ?? null,
    isFsbo_eq: filters.isFsbo_eq ?? null,
    isWheelchairAccessible_eq: filters.isWheelchairAccessible_eq ?? null,
    landSurface_gte: filters.landSurface_gte ?? null,
    landSurface_lte: filters.landSurface_lte ?? null,
    livingSurface_gte: filters.livingSurface_gte ?? null,
    livingSurface_lte: filters.livingSurface_lte ?? null,
    numberOfRooms_gte: filters.numberOfRooms_gte ?? null,
    numberOfRooms_lte: filters.numberOfRooms_lte ?? null,
    grossRentRoomMonthly_gte: filters.grossRentRoomMonthly_gte ?? null,
    grossRentRoomMonthly_lte: filters.grossRentRoomMonthly_lte ?? null,
    createdAt_gte: filters.createdAt_gte,
    salePricePerLandSurface_gte: filters.salePricePerLandSurface_gte ?? null,
    salePricePerLandSurface_lte: filters.salePricePerLandSurface_lte ?? null,
    salePricePerLivingSurface_gte:
      filters.salePricePerLivingSurface_gte ?? null,
    salePricePerLivingSurface_lte:
      filters.salePricePerLivingSurface_lte ?? null,
    salePrice_gte: filters.salePrice_gte ?? null,
    salePrice_lte: filters.salePrice_lte ?? null,
  };

  const {
    east,
    west,
    north,
    south,
    page,
    sortBy,
    sortDirection,
    showMap,
    ...paramsShape
  } = fullParams;

  return paramsShape;
};

export const paramsToFilters = (
  params: ListingsParams,
): $ElementType<listingsQueryVariables, 'filters'> => {
  const { north, south, east, west } = params;
  const bounds =
    north != null && south != null && east != null && west != null
      ? { north, south, east, west }
      : null;
  const placeSlugs = params.placeSlugs ?? [];
  const portal_in = params.portal_in ?? [];

  return pickBy(
    {
      bounds,
      offerType_eq: params.offerType,
      placeSlugs:
        placeSlugs.length === 0
          ? null
          : placeSlugs.map(({ slug, lang }) => ({ slug, lang })),
      createdAt_gte: params.createdAt_gte,
      compositePropertyType_eq: params.compositePropertyType_eq,
      numberOfRooms_gte: params.numberOfRooms_gte,
      numberOfRooms_lte: params.numberOfRooms_lte,

      portal_in: portal_in.length === 0 ? null : portal_in,
      agencyReference_contains: params.agencyReference_contains,
      agencyNames: params.agencyNames,

      livingSurface_lte: params.livingSurface_lte,
      livingSurface_gte: params.livingSurface_gte,
      landSurface_lte: params.landSurface_lte,
      landSurface_gte: params.landSurface_gte,

      hasBalcony_eq: params.hasBalcony_eq,
      hasGarden_eq: params.hasGarden_eq,
      hasParking_eq: params.hasParking_eq,
      hasElevator_eq: params.hasElevator_eq,
      hasView_eq: params.hasView_eq,
      hasFireplace_eq: params.hasFireplace_eq,
      hasGarage_eq: params.hasGarage_eq,
      isChildFriendly_eq: params.isChildFriendly_eq,
      isWheelchairAccessible_eq: params.isWheelchairAccessible_eq,
      isMinergieCertified_eq: params.isMinergieCertified_eq,
      isNewDevelopment_eq: params.isNewDevelopment_eq,
      isFurnished_eq: params.isFurnished_eq,
      bookOnline_eq: params.bookOnline_eq,
      isFsbo_eq: params.isFsbo_eq,

      salePrice_lte: params.salePrice_lte,
      salePrice_gte: params.salePrice_gte,

      grossRentMonthly_lte: params.grossRentMonthly_lte,
      grossRentMonthly_gte: params.grossRentMonthly_gte,
      grossRentM2yearly_gte: params.grossRentM2yearly_gte,
      grossRentM2yearly_lte: params.grossRentM2yearly_lte,
      grossRentRoomMonthly_gte: params.grossRentRoomMonthly_gte,
      grossRentRoomMonthly_lte: params.grossRentRoomMonthly_lte,

      salePricePerLivingSurface_gte: params.salePricePerLivingSurface_gte,
      salePricePerLivingSurface_lte: params.salePricePerLivingSurface_lte,
      salePricePerLandSurface_gte: params.salePricePerLandSurface_gte,
      salePricePerLandSurface_lte: params.salePricePerLandSurface_lte,
    },
    v => v != null,
  );
};

export const paramsToVariables = (
  params: ListingsParams,
  query?: {
    uid?: string,
    lang?: string,
    ...
  },
): listingsQueryVariables => {
  const lngShort = localeToPlaceLang(query?.lang);

  return {
    uid: query?.uid ?? '',
    lang: query?.lang ?? '',
    lngShort,
    sortBy: params.sortBy,
    sortDirection: params.sortDirection,
    propertyMainType: params.compositePropertyType_eq,
    numberOfRooms: params.numberOfRooms_gte,
    offerType: params.offerType,
    ...getSEODataGraphQLArgs(params),
    // first and offset are used in map fragment
    // first: POINTS_ON_MAP_COUNT,
    // offset: params.page != null ? params.page * ITEMS_PER_PAGE : 0,
    // page is used for cards on pages
    page: {
      number: params.page ?? 0,
      count: getItemsPerPage(params.offerType, params.compositePropertyType_eq),
    },
    filters: paramsToFilters(params),
    includeEn: true,
    includeFr: true,
    includeDe: false,
    includeIt: false,
    includeEs: false,
  };
};
